import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  DASHBOARD_KNOWLEDGE_ARTICLES,
  filterKnowledgeArticles,
} from "app/utils/dashboardKnowledge";
import { DashboardKnowledgeArticle } from "app/types/dashboardKnowledge";

type KnowledgePanelProps = {
  open: boolean;
  onClose: () => void;
};

function KnowledgeArticleCard({ article }: { article: DashboardKnowledgeArticle }) {
  const videoUrl = article.videoUrl?.trim() || "";

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: "12px",
        border: "1px solid #e5e9ef",
        bgcolor: "#fff",
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.92rem",
          color: "#111827",
          lineHeight: 1.35,
          mb: 0.5,
        }}
      >
        {article.title}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.82rem",
          color: "#64748b",
          lineHeight: 1.45,
          mb: videoUrl ? 1.25 : 0,
        }}
      >
        {article.subtitle}
      </Typography>
      {videoUrl && (
        <Button
          component="a"
          size="small"
          variant="outlined"
          startIcon={<PlayCircleOutlineIcon sx={{ fontSize: 18 }} />}
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          href={videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            borderColor: "#cfe8d7",
            color: "#2f6f45",
            bgcolor: "#f7fcf9",
            fontWeight: 600,
            fontSize: "0.78rem",
            px: 1.25,
            py: 0.5,
            "&:hover": {
              borderColor: "#7ec597",
              bgcolor: "#eef7f1",
            },
          }}
        >
          Watch tutorial
        </Button>
      )}
    </Box>
  );
}

export function KnowledgePanel({ open, onClose }: KnowledgePanelProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const visibleArticles = useMemo(
    () => filterKnowledgeArticles(searchQuery, DASHBOARD_KNOWLEDGE_ARTICLES),
    [searchQuery]
  );

  const handleClose = () => {
    setSearchQuery("");
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 400 },
          maxWidth: "100%",
          borderLeft: "1px solid #e5e9ef",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fafbfc",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            pt: 1.25,
            pb: 0.75,
            borderBottom: "1px solid #eef1f4",
            bgcolor: "#fff",
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827" }}>
              Knowledge
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#64748b", mt: 0.15 }}>
              How to use the dashboard
            </Typography>
          </Box>
          <IconButton
            aria-label="Close knowledge panel"
            size="small"
            onClick={handleClose}
            sx={{
              width: 32,
              height: 32,
              border: "1px solid #e5e9ef",
              borderRadius: "8px",
              bgcolor: "#fafbfc",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: 1.5, py: 1.25, flexShrink: 0, bgcolor: "#fff" }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search guides"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                bgcolor: "#fafbfc",
                fontSize: "0.875rem",
              },
            }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: 1.5,
            py: 1.25,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {visibleArticles.length > 0 ? (
            visibleArticles.map((article) => (
              <KnowledgeArticleCard key={article.id} article={article} />
            ))
          ) : (
            <Box
              sx={{
                py: 4,
                px: 2,
                textAlign: "center",
                borderRadius: "12px",
                border: "1px dashed #dbe1ea",
                bgcolor: "#fff",
              }}
            >
              <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
                No guides match your search.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
